import { useState, useEffect } from 'react';

import dataApi from '@/shared/services/dataApi';
import { normalizeDataSource } from '@/shared/utils/normalizeDataSource';

/**
 * Loads the real PostGIS data sources from the engine data API
 * (GET /api/data/sources) and returns them normalized for the Advanced Query
 * builder, replacing the former hardcoded mock list.
 *
 * The catalog currently holds ~54 active sources and the API caps `limit` at
 * 100, so a single request returns them all. If the catalog grows past 100,
 * this will need pagination (handled with the builder redesign).
 *
 * @returns {{ sources: Object[], loading: boolean, error: string|null }}
 */
export default function useDataSources() {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Guard against setting state after unmount if the request is still
        // in flight (avoids a React "update on unmounted component" warning).
        let cancelled = false;

        async function fetchSources() {
            setLoading(true);
            setError(null);
            try {
                const res = await dataApi.getSources({ limit: 100 });
                if (!cancelled) {
                    setSources(
                        (res.data_sources || []).map(normalizeDataSource)
                    );
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchSources();
        return () => {
            cancelled = true;
        };
    }, []);

    return { sources, loading, error };
}
